import { Action } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { put, takeLatest } from 'redux-saga/effects'
import { UserModel } from '../models/UserModel'
import { getUserByToken } from './AuthCRUD'
import { SubscriptionModel } from '../models/SubscriptionModel'
// import { setAuthToken } from '../../../helpers/apiClient'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  SubscriptionLoaded: '[Load Subscription] Auth API',
  SetSubscription: '[Set Subscription] Action',
  SubscriptionRequested: '[Request Subscription] Action',
}

const initialAuthState: IAuthState = {
  user: undefined,
  subscription: undefined,
  accessToken: undefined,
  refreshToken: undefined,
}

export interface IAuthState {
  user?: UserModel,
  subscription?: SubscriptionModel,
  accessToken?: string
  refreshToken?: string
}

// Refresh token intentionally omitted from the persist whitelist -- it lives in
// an HttpOnly cookie now (PR-I-50). Leaving it in the whitelist would
// re-introduce the XSS-to-account-takeover path we just closed.
export const reducer = persistReducer(
  { storage, key: 'v100-demo1-auth', whitelist: ['user', 'subscription', 'accessToken'] },
  (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
    switch (action.type) {
      case actionTypes.Login: {
        return {
          ...state,
          accessToken: action.payload?.accessToken,
          refreshToken: action.payload?.refreshToken,
          user: undefined,
          subscription: undefined,
        }
      }

      case actionTypes.Register: {
        const accessToken = action.payload?.accessToken
        return { accessToken, user: undefined }
      }

      case actionTypes.Logout: {
        storage.removeItem('persist:v100-demo1-auth')
        // setAuthToken(null)
        return initialAuthState
      }

      case actionTypes.UserRequested: {
        return { ...state, user: undefined }
      }

      case actionTypes.UserLoaded: {
        const user = action.payload?.user
        const subscription = action.payload?.subscription
        return { ...state, user, ...(subscription !== undefined ? { subscription } : {}) }
      }

      case actionTypes.SetUser: {
        const user = action.payload?.user
        return { ...state, user }
      }

      case actionTypes.SubscriptionLoaded: {
        const subscription = action.payload?.subscription
        return { ...state, subscription }
      }

      case actionTypes.SetSubscription: {
        const subscription = action.payload?.subscription
        return { ...state, subscription }
      }

      default:
        return state
    }
  }
)

export const actions = {
  login: (accessToken: string, refreshToken: string) => ({ type: actionTypes.Login, payload: { accessToken, refreshToken } }),
  register: (accessToken: string) => ({
    type: actionTypes.Register,
    payload: { accessToken },
  }),
  logout: () => ({ type: actionTypes.Logout }),
  requestUser: () => ({
    type: actionTypes.UserRequested,
  }),
  fulfillUser: (user: UserModel, subscription?: SubscriptionModel) => ({ type: actionTypes.UserLoaded, payload: { user, subscription } }),
  setUser: (user: UserModel) => ({ type: actionTypes.SetUser, payload: { user } }),
  fulfillSubscription: (subscription: SubscriptionModel) => ({
    type: actionTypes.SubscriptionLoaded,
    payload: { subscription }
  }),
  setSubscription: (subscription: SubscriptionModel) => ({
    type: actionTypes.SetSubscription,
    payload: { subscription }
  }),
  requestSubscription: () => ({
    type: actionTypes.SubscriptionRequested,
  }),
}

export function* saga() {
  yield takeLatest(actionTypes.UserRequested, function* userRequested(): Generator<any, void, { user: UserModel, subscription?: SubscriptionModel }> {
    try {
      const { user, subscription } = yield getUserByToken()
      if (user) {
        yield put(actions.fulfillUser(user, subscription))
      } else {
        yield put(actions.logout())
      }
    } catch (error) {
      console.error('Saga error fetching user:', error)
      yield put(actions.logout())
    }
  })

  yield takeLatest(actionTypes.SubscriptionRequested, function* subscriptionRequested(): Generator<any, void, { subscription?: SubscriptionModel }> {
    try {
      const { subscription } = yield getUserByToken()
      if (subscription) {
        yield put(actions.fulfillSubscription(subscription))
      }
    } catch (error) {
      console.error('Saga error fetching subscription:', error)
    }
  })
}
